<template>
  <div class="fin-page stock-calendar-page">
    <div class="fin-hero">
      <div class="fin-hero-left">
        <div class="fin-hero-icon"><i class="fa fa-calendar-alt"></i></div>
        <div>
          <h1>Kalendari i stokut</h1>
          <p>Sasia ditore — zgjidh një ose më shumë magazina, ose të gjitha</p>
        </div>
      </div>
      <div class="fin-hero-right">
        <button type="button" class="fin-btn" :disabled="!canLoad || exporting" @click="exportCsv">
          <i class="fa" :class="exporting ? 'fa-circle-notch fa-spin' : 'fa-download'"></i>
          Eksporto
        </button>
        <button type="button" class="fin-btn primary" :disabled="!canLoad || loading" @click="loadMatrix">
          <i class="fa" :class="loading ? 'fa-circle-notch fa-spin' : 'fa-search'"></i>
          {{ loading ? "Duke ngarkuar..." : "Shfaq" }}
        </button>
      </div>
    </div>

    <div class="fin-card stock-cal-filters">
      <div class="stock-cal-filter-row">
        <div class="fin-field stock-cal-warehouse">
          <label>Magazina <span class="req">*</span></label>
          <fin-search-select
            :options="warehouseOptions"
            :model-value="warehouseGuids"
            :multiple="true"
            all-label="Zgjidh magazinën..."
            select-all-label="Të gjitha"
            search-placeholder="Kërko magazinën..."
            @update:modelValue="onWarehouseChange"
          />
        </div>

        <div class="fin-field">
          <label>Periudha</label>
          <div class="fin-segmented">
            <button type="button" :class="{ active: periodMode === 'month' }" @click="setPeriodMode('month')">Muaji</button>
            <button type="button" :class="{ active: periodMode === 'custom' }" @click="setPeriodMode('custom')">Interval</button>
          </div>
        </div>

        <template v-if="periodMode === 'month'">
          <div class="fin-field">
            <label>Muaji</label>
            <select v-model.number="month" class="fin-select" @change="onPeriodChange">
              <option v-for="m in monthOptions" :key="m.value" :value="m.value">{{ m.label }}</option>
            </select>
          </div>
          <div class="fin-field">
            <label>Viti</label>
            <select v-model.number="year" class="fin-select" @change="onPeriodChange">
              <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</option>
            </select>
          </div>
        </template>
        <template v-else>
          <div class="fin-field">
            <label>Nga</label>
            <input v-model="dateFrom" type="date" class="fin-input" @change="onPeriodChange" />
          </div>
          <div class="fin-field">
            <label>Deri</label>
            <input v-model="dateTo" type="date" class="fin-input" @change="onPeriodChange" />
          </div>
          <div class="fin-field stock-cal-presets">
            <label>&nbsp;</label>
            <div class="fin-quick-ranges">
              <button type="button" @click="applyPreset('7')">7 ditë</button>
              <button type="button" @click="applyPreset('30')">30 ditë</button>
              <button type="button" @click="applyPreset('month')">Muaji</button>
            </div>
          </div>
        </template>
      </div>

      <div class="stock-cal-filter-row">
        <div class="fin-field">
          <label>Kërko produktin</label>
          <input v-model="search" class="fin-input" type="text" placeholder="Kod / emër / barkod..." @input="scheduleReload" />
        </div>
        <div class="fin-field">
          <label>Sasia min</label>
          <input v-model.number="qtyMin" class="fin-input" type="number" min="0" step="1" placeholder="—" @change="scheduleReload" />
        </div>
        <div class="fin-field">
          <label>Sasia max</label>
          <input v-model.number="qtyMax" class="fin-input" type="number" min="0" step="1" placeholder="—" @change="scheduleReload" />
        </div>
        <div class="fin-field">
          <label>Alert &lt; (e kuqe)</label>
          <input v-model.number="thresholdLow" class="fin-input" type="number" min="0" step="1" @change="scheduleReload" />
        </div>
        <div class="fin-field">
          <label>Alert &gt; (e kuqe)</label>
          <input v-model="thresholdHighInput" class="fin-input" type="number" min="0" step="1" placeholder="opsionale" @change="scheduleReload" />
        </div>
      </div>

      <div class="stock-cal-legend">
        <span class="leg-swatch stock-low"></span> Nën kufirin e ulët ({{ thresholdLow }})
        <span v-if="thresholdHigh != null" class="leg-swatch stock-high"></span>
        <span v-if="thresholdHigh != null">Mbi kufirin e lartë ({{ thresholdHigh }})</span>
        <span class="muted">· Max interval {{ RANGE_MAX_DAYS }} ditë</span>
      </div>
    </div>

    <div v-if="errorMessage" class="fin-warn">
      <i class="fa fa-circle-info" style="margin-top: 2px"></i>
      <span>{{ errorMessage }}</span>
      <button type="button" class="fin-link-btn" style="margin-left: auto" @click="errorMessage = ''">Mbyll</button>
    </div>
    <div v-if="infoMessage" class="fin-warn is-info">
      <i class="fa fa-circle-check" style="margin-top: 2px"></i>
      <span>{{ infoMessage }}</span>
      <button type="button" class="fin-link-btn" style="margin-left: auto" @click="infoMessage = ''">Mbyll</button>
    </div>

    <div class="fin-card stock-cal-grid-card">
      <div v-if="!warehouseGuids.length" class="fin-empty stock-cal-empty">
        Zgjidh magazinat, ose Të gjitha, për të shfaqur kalendarin e stokut.
      </div>
      <div v-else-if="!hasLoadedOnce && !loading" class="fin-empty stock-cal-empty">
        Shtyp <strong>Shfaq</strong> për të ngarkuar sasitë ditore.
      </div>
      <template v-else>
        <div class="stock-cal-scroll" @mouseleave="clearHover">
          <table
            v-if="rows.length || loading"
            ref="matrixTable"
            class="stock-cal-table"
            @mouseover="onMatrixMouseOver"
          >
            <thead>
              <tr>
                <th class="sticky-col col-code">Kodi</th>
                <th class="sticky-col col-name">Produkti</th>
                <th class="sticky-col col-cur">Aktuale</th>
                <th
                  v-for="day in days"
                  :key="day.key"
                  class="day-head"
                  :class="{ weekend: day.is_weekend }"
                  :data-col="day.key"
                >
                  <div class="day-num">{{ day.d }}</div>
                  <div class="day-wd">{{ day.weekday }}<span v-if="periodMode === 'custom'"> · {{ day.month_short }}</span></div>
                </th>
              </tr>
            </thead>
            <tbody v-if="loading && !rows.length">
              <tr v-for="n in 8" :key="'sk-' + n" class="fin-skeleton-row">
                <td class="sticky-col col-code"><span class="fin-skeleton-line fin-skeleton-line--sm"></span></td>
                <td class="sticky-col col-name"><span class="fin-skeleton-line fin-skeleton-line--md"></span></td>
                <td class="sticky-col col-cur"><span class="fin-skeleton-line fin-skeleton-line--xs"></span></td>
                <td v-for="i in 10" :key="'skd-' + n + '-' + i"><span class="fin-skeleton-line fin-skeleton-line--xs"></span></td>
              </tr>
            </tbody>
            <tbody v-else>
              <tr v-for="row in rows" :key="row.code">
                <td class="sticky-col col-code">{{ row.code }}</td>
                <td class="sticky-col col-name" :title="row.name">{{ row.name }}</td>
                <td class="sticky-col col-cur">{{ formatQty(row.current_qty) }}</td>
                <td
                  v-for="cell in row.cells"
                  :key="row.code + cell.key"
                  class="day-cell"
                  :class="{
                    'stock-low': cell.flag === 'low',
                    'stock-high': cell.flag === 'high',
                    'has-delta': cell.delta,
                  }"
                  :data-col="cell.key"
                  @click="openCell(row, cell)"
                >
                  {{ formatQty(cell.qty) }}
                </td>
              </tr>
            </tbody>
          </table>
          <div v-if="!loading && !rows.length" class="fin-empty">Asnjë produkt për magazinat / filtrat e zgjedhur.</div>
          <div v-if="loadingMore" class="stock-cal-more"><i class="fa fa-circle-notch fa-spin"></i> Duke ngarkuar më shumë...</div>
        </div>

        <div class="stock-cal-footer">
          <div class="stock-cal-pager" v-if="totalPages > 1">
            <button type="button" class="fin-btn" :disabled="page <= 1 || loading" @click="goPage(page - 1)">←</button>
            <span>{{ page }} / {{ totalPages }} ({{ total }} produkte)</span>
            <button type="button" class="fin-btn" :disabled="page >= totalPages || loading" @click="goPage(page + 1)">→</button>
          </div>
          <div v-else class="muted">{{ total }} produkte</div>
        </div>
      </template>
    </div>

    <div v-if="drawer.open" class="stock-cal-drawer-backdrop" @click.self="closeDrawer">
      <aside class="stock-cal-drawer">
        <header class="stock-cal-drawer-head">
          <div>
            <h2>{{ drawer.product?.code }}</h2>
            <p>{{ drawer.product?.name }}</p>
            <p class="muted">{{ drawer.date }} · {{ warehouseLabel }}</p>
          </div>
          <button type="button" class="fin-btn" @click="closeDrawer"><i class="fa fa-times"></i></button>
        </header>
        <div v-if="drawer.loading" class="fin-loading"><i class="fa fa-circle-notch fa-spin"></i> Duke ngarkuar...</div>
        <div v-else-if="drawer.error" class="fin-warn">{{ drawer.error }}</div>
        <div v-else class="stock-cal-drawer-body">
          <div class="drawer-stats">
            <div>
              <div class="muted">Sasia në fund të ditës</div>
              <div class="stat-val" :class="{ 'stock-low': drawer.flag === 'low', 'stock-high': drawer.flag === 'high' }">
                {{ formatQty(drawer.qty) }}
              </div>
            </div>
            <div>
              <div class="muted">Ndryshimi i ditës</div>
              <div class="stat-val">{{ formatSigned(drawer.day_delta) }}</div>
            </div>
          </div>
          <p class="fin-drawer-section-title">Lëvizjet e ditës</p>
          <table class="fin-mini-table">
            <thead>
              <tr>
                <th>Ora</th>
                <th v-if="warehouseGuids.length > 1">Magazina</th>
                <th style="text-align: right">Nga</th>
                <th style="text-align: right">Në</th>
                <th style="text-align: right">Δ</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="m in drawer.movements" :key="m.id">
                <td>{{ formatDateTime(m.occurred_at) }}</td>
                <td v-if="warehouseGuids.length > 1">{{ movementWarehouse(m) }}</td>
                <td style="text-align: right">{{ formatQty(m.qty_before) }}</td>
                <td style="text-align: right">{{ formatQty(m.qty_after) }}</td>
                <td style="text-align: right" :class="Number(m.delta) >= 0 ? 'fin-cell-in' : 'fin-cell-out'">
                  {{ formatSigned(m.delta) }}
                </td>
              </tr>
              <tr v-if="!drawer.movements.length">
                <td :colspan="warehouseGuids.length > 1 ? 5 : 4" class="fin-empty">Asnjë lëvizje këtë ditë.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </aside>
    </div>
  </div>
</template>

<script>
import FinancaStockService from "@/services/FinancaStock";
import FinSearchSelect from "../components/FinSearchSelect.vue";
import permissionMixin from "@/mixins/permissions-mixin.js";
import formatMixin from "../shared/formatMixin.js";
import "../shared/pageStyles.css";

const MONTH_LABELS = [
  "Janar", "Shkurt", "Mars", "Prill", "Maj", "Qershor",
  "Korrik", "Gusht", "Shtator", "Tetor", "Nëntor", "Dhjetor",
];
const RANGE_MAX_DAYS = 62;

function toYmd(date) {
  const pad = (n) => String(n).padStart(2, "0");
  return date.getFullYear() + "-" + pad(date.getMonth() + 1) + "-" + pad(date.getDate());
}

function monthBounds(year, month) {
  const from = new Date(year, month - 1, 1);
  const to = new Date(year, month, 0);
  return { from: toYmd(from), to: toYmd(to) };
}

function daysInclusive(fromYmd, toYmd) {
  const a = new Date(fromYmd + "T00:00:00");
  const b = new Date(toYmd + "T00:00:00");
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return 0;
  return Math.floor((b - a) / 86400000) + 1;
}

export default {
  name: "StockCalendar",
  components: { FinSearchSelect },
  mixins: [permissionMixin, formatMixin],
  data() {
    const now = new Date();
    return {
      RANGE_MAX_DAYS,
      warehouses: [],
      warehouseGuids: [],
      periodMode: "month",
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      dateFrom: "",
      dateTo: "",
      search: "",
      qtyMin: null,
      qtyMax: null,
      thresholdLow: 10,
      thresholdHighInput: "",
      page: 1,
      perPage: 100,
      total: 0,
      totalPages: 1,
      days: [],
      rows: [],
      loading: false,
      loadingMore: false,
      exporting: false,
      hasLoadedOnce: false,
      errorMessage: "",
      infoMessage: "",
      loadToken: 0,
      reloadTimer: null,
      drawer: {
        open: false,
        loading: false,
        error: "",
        product: null,
        date: "",
        qty: null,
        day_delta: null,
        flag: null,
        movements: [],
      },
    };
  },
  computed: {
    monthOptions() {
      return MONTH_LABELS.map((label, idx) => ({ value: idx + 1, label }));
    },
    yearOptions() {
      const y = new Date().getFullYear();
      const list = [];
      for (let i = y - 3; i <= y + 1; i++) list.push(i);
      return list;
    },
    warehouseOptions() {
      return (this.warehouses || []).map((w) => ({
        value: w.guid,
        label: (w.name || w.code) + (w.code ? " (" + w.code + ")" : ""),
      }));
    },
    warehouseLabel() {
      const ids = this.warehouseGuids || [];
      if (!ids.length) return "";
      if (this.warehouseOptions.length > 1 && ids.length === this.warehouseOptions.length) return "Të gjitha";
      const names = ids.map((id) => {
        const w = (this.warehouses || []).find((x) => x.guid === id);
        return w ? w.name || w.code : id;
      });
      if (names.length <= 2) return names.join(", ");
      return names.length + " magazina";
    },
    canLoad() {
      return this.warehouseGuids.length > 0;
    },
    thresholdHigh() {
      if (this.thresholdHighInput === "" || this.thresholdHighInput == null) return null;
      const n = Number(this.thresholdHighInput);
      return Number.isFinite(n) ? n : null;
    },
    selectedDayCount() {
      if (this.periodMode === "custom") {
        if (!this.dateFrom || !this.dateTo) return 0;
        return daysInclusive(this.dateFrom, this.dateTo);
      }
      const bounds = monthBounds(this.year, this.month);
      return daysInclusive(bounds.from, bounds.to);
    },
  },
  methods: {
    onWarehouseChange(value) {
      const next = Array.isArray(value) ? value.map((id) => String(id)).filter(Boolean) : value ? [String(value)] : [];
      this.warehouseGuids = next;
      this.hasLoadedOnce = false;
      this.rows = [];
      this.days = [];
      this.total = 0;
      this.errorMessage = "";
      this.page = 1;
    },
    setPeriodMode(mode) {
      if (mode === this.periodMode) return;
      if (mode === "custom") {
        const bounds = monthBounds(this.year, this.month);
        this.dateFrom = bounds.from;
        this.dateTo = bounds.to;
      }
      this.periodMode = mode;
      this.page = 1;
      if (this.canLoad && this.hasLoadedOnce) this.loadMatrix();
    },
    applyPreset(kind) {
      const today = new Date();
      if (kind === "month") {
        const bounds = monthBounds(today.getFullYear(), today.getMonth() + 1);
        this.dateFrom = bounds.from;
        this.dateTo = bounds.to;
        this.year = today.getFullYear();
        this.month = today.getMonth() + 1;
      } else {
        const days = kind === "7" ? 6 : 29;
        const from = new Date(today);
        from.setDate(today.getDate() - days);
        this.dateFrom = toYmd(from);
        this.dateTo = toYmd(today);
      }
      this.page = 1;
      if (this.canLoad && this.hasLoadedOnce) this.loadMatrix();
    },
    validatePeriod() {
      if (this.periodMode !== "custom") return null;
      if (!this.dateFrom || !this.dateTo) return 'Plotëso datat "Nga" dhe "Deri".';
      const count = daysInclusive(this.dateFrom, this.dateTo);
      if (count <= 0) return 'Data "Nga" nuk mund të jetë pas datës "Deri".';
      if (count > RANGE_MAX_DAYS) {
        return `Periudha është shumë e gjatë (${count} ditë). Maksimumi është ${RANGE_MAX_DAYS} ditë.`;
      }
      return null;
    },
    periodPayload() {
      if (this.periodMode === "custom") {
        return { date_from: this.dateFrom, date_to: this.dateTo };
      }
      return { year: this.year, month: this.month };
    },
    filterPayload() {
      const f = {};
      if (this.search) f.search = this.search;
      if (this.qtyMin != null && this.qtyMin !== "") f.qty_min = this.qtyMin;
      if (this.qtyMax != null && this.qtyMax !== "") f.qty_max = this.qtyMax;
      if (this.thresholdLow != null && this.thresholdLow !== "") f.threshold_low = this.thresholdLow;
      if (this.thresholdHigh != null) f.threshold_high = this.thresholdHigh;
      return f;
    },
    scheduleReload() {
      if (!this.canLoad || !this.hasLoadedOnce) return;
      if (this.reloadTimer) clearTimeout(this.reloadTimer);
      this.reloadTimer = setTimeout(() => {
        this.page = 1;
        this.loadMatrix();
      }, 350);
    },
    onPeriodChange() {
      this.page = 1;
      if (this.canLoad && this.hasLoadedOnce) this.loadMatrix();
    },
    goPage(p) {
      this.page = p;
      this.loadMatrix();
    },
    clearHover() {
      this.setHoverCol(null);
    },
    onMatrixMouseOver(e) {
      const el = e.target.closest("td.day-cell, th.day-head");
      if (!el || !this.$refs.matrixTable || !this.$refs.matrixTable.contains(el)) {
        return;
      }
      const col = el.getAttribute("data-col");
      if (!col) return;
      this.setHoverCol(col);
    },
    setHoverCol(col) {
      if (col === this._hoverCol) return;
      if (this._hoverColEls && this._hoverColEls.length) {
        for (let i = 0; i < this._hoverColEls.length; i++) {
          this._hoverColEls[i].classList.remove("col-hover");
        }
      }
      this._hoverCol = col;
      this._hoverColEls = null;
      if (!col || !this.$refs.matrixTable) return;
      const escaped =
        typeof CSS !== "undefined" && CSS.escape ? CSS.escape(col) : String(col).replace(/"/g, '\\"');
      this._hoverColEls = this.$refs.matrixTable.querySelectorAll(`[data-col="${escaped}"]`);
      for (let i = 0; i < this._hoverColEls.length; i++) {
        this._hoverColEls[i].classList.add("col-hover");
      }
    },
    matrixRequestBody(serverPage, chunkSize) {
      return {
        warehouse_guids: this.warehouseGuids,
        ...this.periodPayload(),
        page: serverPage,
        per_page: chunkSize,
        ...this.filterPayload(),
      };
    },
    async loadMatrix() {
      if (!this.canLoad) {
        this.errorMessage = "Zgjidh të paktën një magazinë.";
        return;
      }
      const periodError = this.validatePeriod();
      if (periodError) {
        this.errorMessage = periodError;
        this.rows = [];
        this.days = [];
        return;
      }

      const token = ++this.loadToken;
      const target = this.perPage;
      let chunk = 25;
      if (this.selectedDayCount > 40) chunk = 20;
      if (this.selectedDayCount > 55) chunk = 15;

      this.loading = true;
      this.loadingMore = false;
      this.errorMessage = "";
      this.hasLoadedOnce = true;
      this.clearHover();

      let collected = [];
      let serverPage = Math.max(1, Math.ceil(((this.page - 1) * target + 1) / chunk));
      const pagesNeeded = Math.ceil(target / chunk);
      let meta = null;

      try {
        for (let i = 0; i < pagesNeeded; i++) {
          if (token !== this.loadToken) return;
          if (i > 0) this.loadingMore = true;

          const data = await FinancaStockService.getCalendarMatrix(this.matrixRequestBody(serverPage + i, chunk));
          if (token !== this.loadToken) return;

          meta = data;
          const batch = data.rows || [];
          collected = collected.concat(batch);

          const totalPagesServer = data.total_pages || 1;
          if (serverPage + i >= totalPagesServer || batch.length < chunk) break;
          if (collected.length >= target) break;
        }

        this.days = meta?.days || [];
        this.total = meta?.total || 0;
        this.totalPages = Math.max(1, Math.ceil(this.total / target));
        this.rows = collected.slice(0, target);
      } catch (e) {
        if (token !== this.loadToken) return;
        this.rows = [];
        this.days = [];
        this.total = 0;
        this.errorMessage = e?.response?.data?.message || "Nuk u ngarkua kalendari.";
      } finally {
        if (token === this.loadToken) {
          this.loading = false;
          this.loadingMore = false;
        }
      }
    },
    async openCell(row, cell) {
      this.drawer = {
        open: true,
        loading: true,
        error: "",
        product: { code: row.code, name: row.name, guid: row.guid },
        date: cell.key,
        qty: cell.qty,
        day_delta: cell.delta,
        flag: cell.flag,
        movements: [],
      };
      try {
        const data = await FinancaStockService.getCalendarCell({
          product_code: row.code,
          warehouse_guids: this.warehouseGuids,
          date: cell.key,
          ...this.filterPayload(),
        });
        this.drawer.loading = false;
        this.drawer.product = data.product || this.drawer.product;
        this.drawer.qty = data.qty;
        this.drawer.day_delta = data.day_delta;
        this.drawer.flag = data.flag;
        this.drawer.movements = data.movements || [];
      } catch (e) {
        this.drawer.loading = false;
        this.drawer.error = e?.response?.data?.message || "Nuk u ngarkua detaji.";
      }
    },
    closeDrawer() {
      this.drawer.open = false;
    },
    movementWarehouse(movement) {
      const code = String(movement.warehouse_code || "").toUpperCase();
      const match = (this.warehouses || []).find((w) => {
        const financa = String(w.code_f2 || w.code || "").toUpperCase();
        return financa === code;
      });
      return match ? match.name || code : code;
    },
    async exportCsv() {
      if (!this.canLoad) return;
      const periodError = this.validatePeriod();
      if (periodError) {
        this.errorMessage = periodError;
        this.infoMessage = "";
        return;
      }
      this.exporting = true;
      this.errorMessage = "";
      this.infoMessage = "";
      try {
        const basePayload = {
          warehouse_guids: this.warehouseGuids,
          ...this.periodPayload(),
          ...this.filterPayload(),
        };

        // Part 1 always runs first; response headers tell us if more parts are needed.
        const first = await FinancaStockService.exportCalendar({ ...basePayload, part: 1 });
        const headerTotal = Number(first?.headers?.["x-export-total"]);
        const headerParts = Number(first?.headers?.["x-export-parts"]);
        const headerPartSize = Number(first?.headers?.["x-export-part-size"]);
        const partSize = headerPartSize > 0 ? headerPartSize : 5000;
        const total = headerTotal > 0 ? headerTotal : Number(this.total) || 0;
        const parts =
          headerParts > 0
            ? headerParts
            : total > 0
              ? Math.max(1, Math.ceil(total / partSize))
              : 1;

        FinancaStockService.saveBlobDownload(
          first,
          `kalendari-stokut-pjesa-1-nga-${parts}.csv`
        );

        for (let part = 2; part <= parts; part++) {
          const response = await FinancaStockService.exportCalendar({ ...basePayload, part });
          FinancaStockService.saveBlobDownload(
            response,
            `kalendari-stokut-pjesa-${part}-nga-${parts}.csv`
          );
        }

        if (parts > 1) {
          this.infoMessage =
            `U eksportuan ${parts} skedarë CSV (${total || "…"} produkte, max ${partSize} për skedar). ` +
            `Skedarët: pjesa 1–${parts}.`;
        } else {
          this.infoMessage = `Eksporti përfundoi (${total || this.rows.length || "…"} produkte).`;
        }
      } catch (e) {
        let message = "Eksporti dështoi.";
        const data = e?.response?.data;
        if (data instanceof Blob) {
          try {
            const text = await data.text();
            const parsed = JSON.parse(text);
            message = parsed.message || message;
          } catch (_) {
            /* ignore */
          }
        } else if (data?.message) {
          message = data.message;
        }
        this.errorMessage = message;
      } finally {
        this.exporting = false;
      }
    },
    async loadWarehouses() {
      try {
        const data = await FinancaStockService.getFilters();
        this.warehouses = (data.warehouses || []).filter((w) => w && w.code);
      } catch (_) {
        this.warehouses = [];
      }
    },
  },
  async created() {
    if (!this.am_allowed("stock_financa.can_see")) {
      window.location.href = "/home";
      return;
    }
    if (this.$root?.setCurrentRoute) {
      this.$root.setCurrentRoute("stock-financa-kalendar");
    }
    await this.loadWarehouses();
  },
  beforeUnmount() {
    if (this.reloadTimer) clearTimeout(this.reloadTimer);
  },
};
</script>

<style scoped>
.stock-cal-filters {
  margin-bottom: 14px;
}
.stock-cal-filter-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-end;
  margin-bottom: 12px;
}
.stock-cal-filter-row .fin-field {
  min-width: 140px;
  flex: 0 1 auto;
}
.stock-cal-warehouse {
  min-width: 260px;
  flex: 1 1 260px;
}
.stock-cal-presets .fin-quick-ranges {
  display: flex;
  gap: 4px;
}
.req {
  color: #b91c1c;
}
.stock-cal-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  font-size: 12px;
  color: #4b5563;
}
.leg-swatch {
  display: inline-block;
  width: 14px;
  height: 14px;
  border-radius: 3px;
  border: 1px solid #e5e7eb;
}
.leg-swatch.stock-low,
.leg-swatch.stock-high {
  background: #fee2e2;
  border-color: #fca5a5;
}
.muted {
  color: #9ca3af;
}
.stock-cal-empty {
  padding: 48px 16px;
  text-align: center;
}
.stock-cal-scroll {
  overflow: auto;
  max-height: calc(100vh - 340px);
  border: 1px solid #e5e7eb;
  border-radius: 10px;
}
.stock-cal-table {
  border-collapse: separate;
  border-spacing: 0;
  width: max-content;
  min-width: 100%;
  font-size: 12px;
}
.stock-cal-table th,
.stock-cal-table td {
  border-bottom: 1px solid #eef2f7;
  border-right: 1px solid #f3f4f6;
  padding: 6px 8px;
  white-space: nowrap;
  background: #fff;
}
.stock-cal-table thead th {
  position: sticky;
  top: 0;
  z-index: 3;
  background: #f8fafc;
  font-weight: 600;
  color: #374151;
}
.sticky-col {
  position: sticky;
  z-index: 2;
  background: #fff;
}
.stock-cal-table thead .sticky-col {
  z-index: 4;
  background: #f8fafc;
}
.col-code {
  left: 0;
  min-width: 90px;
}
.col-name {
  left: 90px;
  min-width: 180px;
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
}
.col-cur {
  left: 270px;
  min-width: 72px;
  text-align: right;
  box-shadow: 4px 0 8px -6px rgba(15, 23, 42, 0.25);
}
.day-head {
  text-align: center;
  min-width: 48px;
}
.day-head.weekend {
  background: #f9fafb;
}
.day-num {
  font-weight: 700;
}
.day-wd {
  font-size: 10px;
  color: #6b7280;
}
.day-cell {
  text-align: right;
  cursor: pointer;
  font-variant-numeric: tabular-nums;
}
.day-cell:hover {
  outline: 1px solid #93c5fd;
}
.day-cell.stock-low,
.day-cell.stock-high,
.stat-val.stock-low,
.stat-val.stock-high {
  background: #fee2e2 !important;
  color: #991b1b;
  font-weight: 600;
}
.day-cell.has-delta:not(.stock-low):not(.stock-high) {
  font-weight: 600;
}
.stock-cal-table tbody tr:hover > td:not(.day-cell.stock-low):not(.day-cell.stock-high) {
  background: #f8fafc;
}
.col-hover:not(.stock-low):not(.stock-high) {
  background: #eff6ff !important;
}
.day-head.col-hover {
  background: #dbeafe !important;
}
.stock-cal-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 12px;
  gap: 12px;
}
.stock-cal-pager {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
}
.stock-cal-more {
  padding: 8px;
  text-align: center;
  color: #6b7280;
  font-size: 13px;
}
.stock-cal-drawer-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.35);
  z-index: 10050;
  display: flex;
  justify-content: flex-end;
}
.stock-cal-drawer {
  width: min(420px, 100%);
  background: #fff;
  height: 100%;
  box-shadow: -8px 0 24px rgba(15, 23, 42, 0.12);
  display: flex;
  flex-direction: column;
}
.stock-cal-drawer-head {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 16px;
  border-bottom: 1px solid #e5e7eb;
}
.stock-cal-drawer-head h2 {
  margin: 0;
  font-size: 16px;
}
.stock-cal-drawer-head p {
  margin: 2px 0 0;
  font-size: 13px;
  color: #4b5563;
}
.stock-cal-drawer-body {
  padding: 16px;
  overflow: auto;
}
.drawer-stats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 16px;
}
.stat-val {
  font-size: 20px;
  font-weight: 700;
  margin-top: 4px;
}
.fin-drawer-section-title {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  color: #6b7280;
  margin: 0 0 8px;
}
.fin-mini-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}
.fin-mini-table th,
.fin-mini-table td {
  border-bottom: 1px solid #eef2f7;
  padding: 6px 4px;
  text-align: left;
}
.fin-cell-in {
  color: #047857;
}
.fin-cell-out {
  color: #b91c1c;
}
</style>
